import { signal } from "@pdfking/reactivity/preact"
import { CreateSingleton } from "@pdfking/tfunc"
import {
  url_parse,
  url_query_decode,
} from "@pdfking/workflow/utils/urlutilities"
import register_dom_update from "../navigation/register_dom_update"
import state from "../state/main"
import urls, { DEFAULT_VIEW } from "./urls"

export interface RouteState {
  name: string
  props: Record<string, any>
  component: any
}

const remove_hash = () =>
  history.replaceState(
    "",
    document.title,
    window.location.pathname + window.location.search
  )

const current_route = signal<RouteState>({
  component: null,
  name: DEFAULT_VIEW,
  props: {},
})

function parse_hash(hash: string): {
  name: string
  query: Record<string, string>
  rest: string
} {
  // hash format: #/menu?url=... -> extract everything after #
  const path = hash.startsWith("#") ? hash.slice(1) : hash
  if (!path || path === "/") return { name: DEFAULT_VIEW, query: {}, rest: "" }

  const parsed = url_parse(path)
  const pathname = parsed.pathname.replace(/^\//, "")
  const parts = pathname.split("/")
  const name = parts[0] || DEFAULT_VIEW
  const rest = parts.slice(1).join("/")
  const query = url_query_decode(parsed.search?.slice(1))
  return { name, query, rest }
}

async function resolve_route(hash: string) {
  const { name, query, rest } = parse_hash(hash)
  const route_def = urls[name]

  if (!route_def || name === DEFAULT_VIEW) {
    current_route.value = { component: null, name: DEFAULT_VIEW, props: {} }
    remove_hash()
    return
  }

  const prev = current_route.value

  // Track events
  if (prev.name !== name) {
    if (prev.name !== DEFAULT_VIEW)
      state().track_event(`${prev.name}_close`, prev.name)
    if (name !== DEFAULT_VIEW) state().track_event(`${name}_start`, name)
  } else {
    if (name !== DEFAULT_VIEW) state().track_event(`${name}_update`, name)
  }

  try {
    const component_module = await route_def.component()
    const component = component_module[route_def.export_name]
    const props = route_def.parse_props(rest, query)

    current_route.value = { component, name, props }
  } catch (e) {
    console.error("Route resolve error:", e)
  }
}

register_dom_update({
  before: () => {
    if (current_route.value.name !== DEFAULT_VIEW) {
      current_route.value = { component: null, name: DEFAULT_VIEW, props: {} }
      remove_hash()
    }
  },
  init: () => {
    resolve_route(window.location.hash)
    window.addEventListener("hashchange", () =>
      resolve_route(window.location.hash)
    )
  },
})

const get_router = CreateSingleton(() => ({
  get current() {
    return current_route
  },
  push(path: string) {
    window.location.hash = path.startsWith("/") ? path : `/${path}`
  },
}))

export { current_route }
export default get_router
